import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { LaunchSignup } from "@/components/LaunchSignup";
import { adminApi } from "@/lib/api";
import {
  Ban, Download, FileWarning, Lock, Mail, MailX, RotateCcw, ShieldCheck, Sparkles, Undo2, Users,
} from "lucide-react";

/** Phase 12 — Admin → BETA & EARLY ACCESS.
 * Controls the public soft launch. NEVER bypasses the Production Launch Gate:
 * the board below reads the real gate state and beta toggles cannot unlock LIVE. */

const BOARD_ROWS = [
  { key: "public_website", label: "PUBLIC WEBSITE" },
  { key: "beta_mode", label: "BETA MODE" },
  { key: "early_access_signup", label: "EARLY ACCESS SIGNUP" },
  { key: "community_voice", label: "COMMUNITY VOICE" },
  { key: "candidate_information", label: "CANDIDATE INFORMATION" },
  { key: "email_mode", label: "EMAIL MODE" },
  { key: "full_election_implementation", label: "FULL ELECTION IMPLEMENTATION" },
  { key: "production_launch_gate", label: "PRODUCTION LAUNCH GATE" },
];

const boardTone = (key, value) => {
  if (["ONLINE", "OPEN", "AVAILABLE", "AVAILABLE AS PUBLISHED"].includes(value))
    return "bg-emerald-50 text-emerald-800 border-emerald-300";
  if (value === "SIMULATED") return "bg-amber-50 text-amber-900 border-amber-300";
  if (["LOCKED", "NOT YET LIVE"].includes(value)) return "bg-slate-100 text-slate-700 border-slate-300";
  return "bg-secondary text-foreground border-slate-300";
};

const STATUS_TONES = {
  subscribed: "bg-emerald-50 text-emerald-800 border-emerald-300",
  verified: "bg-emerald-50 text-emerald-800 border-emerald-300",
  pending_verification: "bg-amber-50 text-amber-900 border-amber-300",
  unsubscribed: "bg-slate-100 text-slate-600 border-slate-300",
  suppressed: "bg-red-50 text-red-800 border-red-300",
  bounced: "bg-red-50 text-red-800 border-red-300",
};

const MSG_FIELDS = [
  ["banner_title", "Banner title"],
  ["banner_body", "Banner body"],
  ["hero_title", "Homepage hero title"],
  ["hero_body", "Homepage hero body"],
  ["hero_signup_line", "Hero signup line"],
  ["mobile_title", "Mobile banner title"],
  ["mobile_body", "Mobile banner body"],
  ["signup_success_title", "Signup success title"],
  ["signup_success_body", "Signup success body"],
  ["consent_label", "Consent checkbox label"],
  ["consent_fine_print", "Consent fine print"],
  ["dont_miss_title", "Don't-miss section title"],
  ["dont_miss_body", "Don't-miss section body"],
  ["preview_notice", "Declaration BETA/PREVIEW notice"],
];

export default function Beta() {
  const [data, setData] = useState(null);
  const [subs, setSubs] = useState({ items: [], total: 0 });
  const [feedback, setFeedback] = useState({ items: [], total: 0 });
  const [announcement, setAnnouncement] = useState(null);
  const [msgDraft, setMsgDraft] = useState({});

  const load = useCallback(() => {
    adminApi.get("/admin/beta").then((r) => {
      setData(r.data);
      setMsgDraft(r.data.settings.messaging || {});
    }).catch(() => toast.error("Could not load beta overview."));
    adminApi.get("/admin/beta/subscribers").then((r) => setSubs(r.data)).catch(() => {});
    adminApi.get("/admin/beta/feedback").then((r) => setFeedback(r.data)).catch(() => {});
    adminApi.get("/admin/beta/launch-announcement").then((r) => setAnnouncement(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const patchSettings = async (patch, label) => {
    try {
      await adminApi.patch("/admin/beta/settings", patch);
      toast.success(`${label} updated.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };

  const saveMessaging = async () => {
    await patchSettings({ messaging: msgDraft }, "Beta messaging");
  };

  const subAction = async (id, action) => {
    try {
      await adminApi.patch(`/admin/beta/subscribers/${id}`, { action });
      toast.success(`Subscriber ${action.replace("_", " ")} recorded.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
  };

  const fbAction = async (id, status) => {
    try {
      await adminApi.patch(`/admin/beta/feedback/${id}`, { status });
      load();
    } catch { toast.error("Could not update feedback."); }
  };

  const exportCsv = async () => {
    try {
      const r = await adminApi.get("/admin/beta/subscribers/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "wyv-launch-subscribers.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Subscriber CSV exported (authorized administrative use only).");
    } catch { toast.error("Export failed."); }
  };

  const saveAnnouncement = async () => {
    try {
      await adminApi.patch("/admin/beta/launch-announcement", {
        subject: announcement.subject, body: announcement.body,
      });
      toast.success("Draft saved. Status remains DRAFT — DO NOT SEND.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const { settings, board, stats, feedback: fbCounts } = data;

  return (
    <div data-testid="admin-beta-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground inline-flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-accent" /> Beta &amp; Early Access
          </h1>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Public BETA soft launch controls. This page can never bypass the Production Launch
            Gate — LIVE email and full election implementation remain locked until every strict
            production gate is green.
          </p>
        </div>
        <Badge variant="outline"
               className={board.beta_checklist_open ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-amber-50 text-amber-900 border-amber-300"}
               data-testid="beta-checklist-badge">
          BETA CHECKLIST: {board.beta_checklist_open ? "OPEN" : "NOT YET OPEN"} (see Launch Gate)
        </Badge>
      </div>

      {/* STATUS BOARD — §28 */}
      <Card className="mb-6" data-testid="beta-status-board">
        <CardContent className="p-5">
          <h2 className="font-heading font-bold text-sm tracking-wide mb-3 inline-flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-accent" /> WITNESSYOURVOTE STATUS
          </h2>
          <div className="grid sm:grid-cols-2 gap-2">
            {BOARD_ROWS.map((row) => (
              <div key={row.key} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2"
                   data-testid={`beta-board-${row.key}`}>
                <span className="text-xs font-semibold text-foreground">{row.label}</span>
                <Badge variant="outline" className={`text-[10px] ${boardTone(row.key, board[row.key])}`}>
                  {["LOCKED", "NOT YET LIVE"].includes(board[row.key]) && <Lock className="h-3 w-3 mr-1" />}
                  {board[row.key]}
                </Badge>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">
            BETA (explore, test, learn, sign up) and the OFFICIAL PUBLIC IMPLEMENTATION are two
            different states. Never say "fully live" during BETA — say "now open in BETA."
          </p>
        </CardContent>
      </Card>

      {/* CONTROLS */}
      <Card className="mb-6" data-testid="beta-controls-card">
        <CardContent className="p-5">
          <h2 className="font-heading font-bold text-sm mb-3">Beta controls</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3">
              <span className="text-xs font-semibold">BETA MODE</span>
              <Switch checked={!!settings.beta_mode}
                      onCheckedChange={(v) => patchSettings({ beta_mode: v }, "Beta mode")}
                      data-testid="beta-toggle-mode" />
            </label>
            <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3">
              <span className="text-xs font-semibold">Public beta banner</span>
              <Switch checked={!!settings.banner_enabled}
                      onCheckedChange={(v) => patchSettings({ banner_enabled: v }, "Beta banner")}
                      data-testid="beta-toggle-banner" />
            </label>
            <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3">
              <div>
                <span className="text-xs font-semibold block">Public signup counter</span>
                <span className="text-[10px] text-muted-foreground">Real count only ({stats.active_real}) — keep off until useful</span>
              </div>
              <Switch checked={!!settings.counter_enabled}
                      onCheckedChange={(v) => patchSettings({ counter_enabled: v }, "Public counter")}
                      data-testid="beta-toggle-counter" />
            </label>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="signups">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="signups" data-testid="beta-tab-signups">
            <Users className="h-3.5 w-3.5 mr-1.5" /> Early Access ({stats.total})
          </TabsTrigger>
          <TabsTrigger value="feedback" data-testid="beta-tab-feedback">
            <FileWarning className="h-3.5 w-3.5 mr-1.5" /> Beta Feedback ({fbCounts.new} new)
          </TabsTrigger>
          <TabsTrigger value="messaging" data-testid="beta-tab-messaging">Messaging</TabsTrigger>
          <TabsTrigger value="preview" data-testid="beta-tab-preview">Signup preview</TabsTrigger>
          <TabsTrigger value="announcement" data-testid="beta-tab-announcement">
            <Mail className="h-3.5 w-3.5 mr-1.5" /> Launch announcement
          </TabsTrigger>
        </TabsList>

        {/* EARLY ACCESS */}
        <TabsContent value="signups" className="mt-4 space-y-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Card><CardContent className="p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Total signups</p>
              <p className="font-heading font-bold text-2xl tabular-nums" data-testid="beta-stat-total">{stats.total}</p>
              <p className="text-[10px] text-muted-foreground">{stats.test_count} test-flagged</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Active (real)</p>
              <p className="font-heading font-bold text-2xl tabular-nums text-emerald-700" data-testid="beta-stat-active">{stats.active_real}</p>
              <p className="text-[10px] text-muted-foreground">verified: {stats.verified} (verification activates in TEST mode)</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Unsubscribed</p>
              <p className="font-heading font-bold text-2xl tabular-nums">{stats.by_status.unsubscribed || 0}</p>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Suppressed / bounced</p>
              <p className="font-heading font-bold text-2xl tabular-nums">{(stats.by_status.suppressed || 0) + (stats.by_status.bounced || 0)}</p>
            </CardContent></Card>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <Card><CardContent className="p-4">
              <p className="text-xs font-semibold mb-2">Municipality distribution</p>
              {stats.municipality_distribution.length === 0
                ? <p className="text-xs text-muted-foreground italic">No municipality data yet.</p>
                : stats.municipality_distribution.map((m) => (
                  <div key={m.municipality} className="flex justify-between text-xs py-0.5">
                    <span className="truncate">{m.municipality}</span><span className="tabular-nums font-medium">{m.count}</span>
                  </div>))}
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-xs font-semibold mb-2">Ward distribution</p>
              {stats.ward_distribution.length === 0
                ? <p className="text-xs text-muted-foreground italic">No ward data yet.</p>
                : stats.ward_distribution.map((w, i) => (
                  <div key={i} className="flex justify-between text-xs py-0.5">
                    <span className="truncate">{w.municipality} — {w.ward}</span><span className="tabular-nums font-medium">{w.count}</span>
                  </div>))}
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <p className="text-xs font-semibold mb-2">Signup trend (30 days)</p>
              {stats.trend.length === 0
                ? <p className="text-xs text-muted-foreground italic">No signups in the last 30 days.</p>
                : stats.trend.map((t) => (
                  <div key={t.date} className="flex justify-between text-xs py-0.5">
                    <span>{t.date}</span><span className="tabular-nums font-medium">{t.count}</span>
                  </div>))}
            </CardContent></Card>
          </div>

          <Card>
            <CardContent className="p-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h3 className="font-heading font-bold text-sm">Subscribers (admin-only — never public)</h3>
                <Button size="sm" variant="outline" onClick={exportCsv} data-testid="beta-export-csv">
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Export privacy-safe CSV
                </Button>
              </div>
              <Table data-testid="beta-subscribers-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead><TableHead>Name</TableHead>
                    <TableHead>Municipality / Ward</TableHead><TableHead>Status</TableHead>
                    <TableHead>Consent</TableHead><TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subs.items.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-6">No subscribers yet.</TableCell></TableRow>
                  )}
                  {subs.items.map((s) => (
                    <TableRow key={s.id} data-testid={`beta-sub-row-${s.id}`}>
                      <TableCell className="font-mono text-xs">{s.email}{s.is_test && <Badge variant="outline" className="ml-1.5 text-[9px]">TEST</Badge>}</TableCell>
                      <TableCell className="text-xs">{s.first_name || "—"}</TableCell>
                      <TableCell className="text-xs">{s.municipality || "—"}{s.ward ? ` / ${s.ward}` : ""}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[10px] ${STATUS_TONES[s.status] || ""}`}>{s.status}</Badge>
                      </TableCell>
                      <TableCell>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="ghost" className="h-7 text-[11px] px-2" data-testid={`beta-sub-consent-${s.id}`}>View</Button>
                          </DialogTrigger>
                          <DialogContent className="bg-white max-w-md">
                            <DialogHeader>
                              <DialogTitle>Consent record</DialogTitle>
                              <DialogDescription>Stored verbatim at signup.</DialogDescription>
                            </DialogHeader>
                            <div className="text-xs space-y-1.5">
                              <p><strong>Consent text:</strong> "{s.consent_text}"</p>
                              <p><strong>Consent timestamp:</strong> {s.consent_at}</p>
                              <p><strong>Source page:</strong> {s.source_page || "—"}</p>
                              <p><strong>Signup date:</strong> {s.created_at}</p>
                              <p><strong>Verification:</strong> {s.verification_status}</p>
                              {s.unsubscribed_at && <p><strong>Unsubscribed:</strong> {s.unsubscribed_at}</p>}
                            </div>
                          </DialogContent>
                        </Dialog>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {s.status !== "suppressed" && (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-destructive"
                                    onClick={() => subAction(s.id, "suppress")} title="Suppress invalid signup"
                                    data-testid={`beta-sub-suppress-${s.id}`}>
                              <Ban className="h-3 w-3" />
                            </Button>
                          )}
                          {["subscribed", "verified", "pending_verification"].includes(s.status) && (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                                    onClick={() => subAction(s.id, "unsubscribe")} title="Unsubscribe"
                                    data-testid={`beta-sub-unsub-${s.id}`}>
                              <MailX className="h-3 w-3" />
                            </Button>
                          )}
                          {["unsubscribed", "suppressed", "bounced"].includes(s.status) && (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                                    onClick={() => subAction(s.id, "reactivate")} title="Reactivate"
                                    data-testid={`beta-sub-react-${s.id}`}>
                              <Undo2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-[10px] text-muted-foreground mt-2">
                EMAIL MODE: {board.email_mode} — welcome messages are logged only; nothing external is
                sent while SIMULATED. When TEST/LIVE is later authorized, this list is used strictly
                according to each subscriber's consent (respecting unsubscribes, suppressions and bounces).
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* FEEDBACK */}
        <TabsContent value="feedback" className="mt-4">
          <Card>
            <CardContent className="p-5">
              <h3 className="font-heading font-bold text-sm mb-3">Beta feedback ({feedback.total})</h3>
              {feedback.items.length === 0 && <p className="text-sm text-muted-foreground italic">No feedback yet.</p>}
              <div className="space-y-3" data-testid="beta-feedback-list">
                {feedback.items.map((f) => (
                  <div key={f.id} className="rounded-lg border border-slate-200 p-3" data-testid={`beta-feedback-row-${f.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-800 border-blue-200">{f.category_label}</Badge>
                      <Badge variant="outline" className={`text-[10px] ${f.status === "new" ? "bg-amber-50 text-amber-900 border-amber-300" : "bg-slate-100 text-slate-600 border-slate-300"}`}>{f.status}</Badge>
                      {f.page && <span className="text-[10px] text-muted-foreground font-mono">{f.page}</span>}
                      <span className="text-[10px] text-muted-foreground ml-auto">{new Date(f.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-foreground mt-1.5 break-words">{f.comment}</p>
                    {f.email && <p className="text-[10px] text-muted-foreground mt-1">Reply-to (admin-only): {f.email}</p>}
                    <div className="flex gap-2 mt-2">
                      {f.status === "new" && (
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => fbAction(f.id, "reviewed")}
                                data-testid={`beta-feedback-review-${f.id}`}>Mark reviewed</Button>
                      )}
                      {f.status !== "resolved" && (
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => fbAction(f.id, "resolved")}
                                data-testid={`beta-feedback-resolve-${f.id}`}>Mark resolved</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* MESSAGING */}
        <TabsContent value="messaging" className="mt-4">
          <Card>
            <CardContent className="p-5">
              <h3 className="font-heading font-bold text-sm mb-1">Approved Beta messaging</h3>
              <p className="text-[11px] text-muted-foreground mb-4">
                Shown on the public banner, homepage hero, signup and declaration preview notices.
                Keep the BETA vs official-implementation distinction clear — never claim "fully live."
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {MSG_FIELDS.map(([key, label]) => (
                  <div key={key}>
                    <Label className="text-[11px]">{label}</Label>
                    <Textarea className="mt-1 text-xs min-h-9" rows={key.includes("body") || key.includes("print") ? 3 : 1}
                              value={msgDraft[key] || ""}
                              onChange={(e) => setMsgDraft((d) => ({ ...d, [key]: e.target.value }))}
                              data-testid={`beta-msg-${key}`} />
                  </div>
                ))}
              </div>
              <Button className="mt-4 bg-primary hover:bg-primary/90" onClick={saveMessaging} data-testid="beta-msg-save">
                Save messaging
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SIGNUP PREVIEW */}
        <TabsContent value="preview" className="mt-4">
          <Card>
            <CardContent className="p-5">
              <h3 className="font-heading font-bold text-sm mb-1">Signup component preview</h3>
              <p className="text-[11px] text-muted-foreground mb-4">
                This is the live component — submitting here creates a real record. Use an
                <span className="font-mono"> @test.example</span> address so it is flagged as test data.
              </p>
              <div className="max-w-lg" data-testid="beta-signup-preview">
                <LaunchSignup variant="full" sourcePage="/admin/beta (preview)" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LAUNCH ANNOUNCEMENT DRAFT */}
        <TabsContent value="announcement" className="mt-4">
          <Card className="border-2 border-amber-300">
            <CardContent className="p-5">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <h3 className="font-heading font-bold text-sm">Official launch announcement</h3>
                <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-400 font-bold text-[10px]"
                       data-testid="beta-announcement-status">
                  {announcement?.status || "DRAFT — DO NOT SEND"}
                </Badge>
              </div>
              <p className="text-[11px] text-muted-foreground mb-3">
                There is deliberately <strong>no send button</strong>. Sending becomes possible only after
                LIVE email is unlocked through the Production Launch Gate — and only to validly consenting,
                eligible subscribers (respecting unsubscribes, suppressions, bounces and verification).
                Never simply email every address ever stored.
              </p>
              {announcement && (
                <>
                  <Label className="text-[11px]">Subject</Label>
                  <Input className="mt-1" value={announcement.subject}
                         onChange={(e) => setAnnouncement((a) => ({ ...a, subject: e.target.value }))}
                         data-testid="beta-announcement-subject" />
                  <Label className="text-[11px] mt-3 block">Body (draft — wording reviewed before LIVE)</Label>
                  <Textarea className="mt-1 font-mono text-xs" rows={12} value={announcement.body}
                            onChange={(e) => setAnnouncement((a) => ({ ...a, body: e.target.value }))}
                            data-testid="beta-announcement-body" />
                  <p className="text-[10px] text-muted-foreground mt-2 italic">{announcement.audience_policy}</p>
                  <Button className="mt-3" variant="outline" onClick={saveAnnouncement} data-testid="beta-announcement-save">
                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Save draft
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
